/**
 * Alles, was Rangliste und Tipp-Vergleich über eine Liga-Runde wissen müssen —
 * einmal geladen, von beiden benutzt.
 *
 * Die Rangliste braucht Spieltipps und Spielplan für die Klassik-Wertung, das
 * Vergleichs-Panel dieselben Daten für die Anzeige. Ohne gemeinsame Quelle
 * stellen zwei Geschwister-Komponenten dieselben vier Anfragen doppelt. Der
 * Cache liegt auf der Promise, nicht auf dem Ergebnis: Starten beide im selben
 * Rendern, teilen sie sich denselben Flug.
 *
 * Bewusst kein globaler Store — der Cache lebt für die Dauer der Sitzung, und
 * `invalidateLeagueTipData` räumt ihn, wenn ein Tipp geschrieben wurde.
 */

import { useEffect, useState } from "react";
import { matchTipService } from "../services/MatchTipService.ts";
import { leagueFixtureService } from "../services/LeagueFixtureService.ts";
import { specialQuestionService } from "../services/SpecialQuestionService.ts";
import type {
  LeagueFixture,
  MatchTipWithProfile,
  SpecialQuestion,
  SpecialQuestionAnswer,
  SpecialQuestionOption,
} from "../types/index.ts";

export interface LeagueTipData {
  matchTips: MatchTipWithProfile[];
  fixtures: LeagueFixture[];
  /** matchday -> Sperrzeit in ms. */
  deadlines: Map<number, number>;
  questions: SpecialQuestion[];
  options: SpecialQuestionOption[];
  /** Nur Antworten auf Fragen, deren Frist abgelaufen ist. */
  answers: SpecialQuestionAnswer[];
  /** Die Fragen, deren Antworten geholt wurden — Autorität für die Anzeige. */
  lockedQuestionIds: number[];
}

const EMPTY: LeagueTipData = {
  matchTips: [],
  fixtures: [],
  deadlines: new Map(),
  questions: [],
  options: [],
  answers: [],
  lockedQuestionIds: [],
};

const cache = new Map<string, Promise<LeagueTipData>>();

const keyOf = (tipRoundId: number, leagueId: number) => `${tipRoundId}:${leagueId}`;

async function load(tipRoundId: number, leagueId: number): Promise<LeagueTipData> {
  const [matchTips, fixtures, deadlines, sq] = await Promise.all([
    matchTipService.getAllTipsForRound(tipRoundId, leagueId),
    leagueFixtureService.getAllFixtures(leagueId),
    leagueFixtureService.getMatchdayDeadlines(leagueId),
    specialQuestionService.listLockedAnswersForRound(tipRoundId, leagueId),
  ]);
  return {
    matchTips,
    fixtures,
    deadlines,
    questions: sq.questions,
    options: sq.options,
    answers: sq.answers,
    lockedQuestionIds: sq.lockedQuestionIds,
  };
}

/** Nach einem geschriebenen Tipp aufrufen, damit der nächste Leser frische Daten sieht. */
export function invalidateLeagueTipData(tipRoundId: number, leagueId: number): void {
  cache.delete(keyOf(tipRoundId, leagueId));
}

export function useLeagueTipData(
  tipRoundId: number | undefined,
  leagueId: number | undefined,
): { data: LeagueTipData; loading: boolean } {
  const [data, setData] = useState<LeagueTipData>(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tipRoundId || !leagueId) {
      setData(EMPTY);
      setLoading(false);
      return;
    }
    const key = keyOf(tipRoundId, leagueId);
    let pending = cache.get(key);
    if (!pending) {
      pending = load(tipRoundId, leagueId).catch((e) => {
        // Ein Fehlschlag darf sich nicht einbrennen — sonst bleibt die Runde
        // für den Rest der Sitzung leer.
        cache.delete(key);
        console.error("useLeagueTipData failed:", e);
        return EMPTY;
      });
      cache.set(key, pending);
    }

    let cancelled = false;
    setLoading(true);
    void pending.then((result) => {
      if (cancelled) return;
      setData(result);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [tipRoundId, leagueId]);

  return { data, loading };
}
